import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';
import type { SessionStatus } from '@claude-remote/shared';
import { StatusBadge } from '../components/Badge.js';
import { api } from '../lib/api.js';
import { ws } from '../lib/ws.js';
import { buildTitle, useDocumentTitle } from '../lib/use-document-title.js';

interface ActiveSessionRow {
  id: string;
  project_id: string;
  project_name: string;
  title: string | null;
  status: SessionStatus;
  started_at: number;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
}

/** Format token count: 1234 → "1.2k", 1234567 → "1.2M", etc. */
function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return (n / 1_000_000).toFixed(2).replace(/\.00$/, '') + 'M';
}

export function ActiveScreen() {
  const [sessions, setSessions] = useState<ActiveSessionRow[]>([]);
  useDocumentTitle(buildTitle('Активные'));
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await api.get<ActiveSessionRow[]>('/api/v1/sessions/active');
      setSessions(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // session_status / permission_* приходят на любую вкладку — рефетчим
    // список, чтобы пользователь видел актуальное состояние всех сессий
    // без ручного обновления. Список короткий, оверхеда нет.
    const unsub = ws.onMessage((msg) => {
      if (
        msg.type === 'session_status' ||
        msg.type === 'permission_request' ||
        msg.type === 'permission_resolved'
      ) {
        void load();
      }
    });
    return unsub;
  }, [load]);

  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, ActiveSessionRow[]>();
    for (const s of sessions) {
      if (!map.has(s.project_name)) {
        map.set(s.project_name, []);
        order.push(s.project_name);
      }
      map.get(s.project_name)!.push(s);
    }
    return order.map((name) => ({ name, items: map.get(name)! }));
  }, [sessions]);

  async function dismiss(id: string) {
    // Обновляем оптимистично — карточка сразу пропадает, рефетч из WS
    // подтвердит. Если запрос упадёт, следующий рефетч вернёт её обратно.
    setSessions((prev) => prev.filter((s) => s.id !== id));
    try {
      await api.post(`/api/v1/sessions/${id}/force-finish`);
    } catch {
      void load();
    }
  }

  return (
    <div className="p-4 space-y-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold pt-2">Активные сессии</h1>

      {loading ? (
        <p className="text-neutral-500 text-sm">Загружаем…</p>
      ) : sessions.length === 0 ? (
        <div className="rounded-2xl border border-neutral-800 p-6 text-center">
          <p className="text-neutral-400">Нет активных сессий</p>
          <p className="text-xs text-neutral-500 mt-1">
            Здесь появятся все живые вкладки — работающие, ждущие
            разрешения, idle и аварийно завершённые (error). Чтобы убрать
            error-карточку — нажми × «закрыть».
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.name} className="space-y-3">
              <h2 className="text-2xl md:text-3xl font-extrabold uppercase tracking-tight text-amber-200 break-words">
                {g.name}
                {g.items.length > 1 && (
                  <span className="ml-2 align-middle text-sm font-medium text-amber-100/70">
                    · {g.items.length}
                  </span>
                )}
              </h2>
              <ul className="space-y-2">
                {g.items.map((s) => {
                  const isError = s.status === 'error';
                  return (
                    <li key={s.id} className="relative">
                      <Link
                        to={`/sessions/${s.id}`}
                        className={`block rounded-2xl border transition p-3 ${
                          isError
                            ? 'border-red-500/40 bg-red-950/20 hover:bg-red-950/30 active:bg-red-950/40'
                            : 'border-neutral-800 bg-neutral-900/60 hover:bg-neutral-900 active:bg-neutral-800/80'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm md:text-base font-medium text-neutral-100 break-words line-clamp-2">
                              {s.title ?? '(без заголовка)'}
                            </div>
                            <div className="text-[11px] text-neutral-500 mt-1 tabular-nums">
                              ${s.cost_usd.toFixed(4)}
                              {(s.input_tokens + s.output_tokens) > 0 && (
                                <> · {formatTokens(s.input_tokens + s.output_tokens)} tok</>
                              )}
                              {' · '}
                              {new Date(s.started_at).toLocaleString('ru-RU', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <StatusBadge status={s.status} />
                            {!isError && (
                              <ChevronRight className="w-4 h-4 text-neutral-600" />
                            )}
                          </div>
                        </div>
                      </Link>
                      {isError && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            void dismiss(s.id);
                          }}
                          title="Отметить просмотренной и убрать"
                          className="absolute top-2 right-2 p-1 rounded-md text-red-300 hover:text-red-100 hover:bg-red-500/20"
                          aria-label="закрыть"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
